import { ng } from "../ngds";

/**
 * Asset-database materials are stored per Eurocode national annex, but a material is not "of" an
 * annex: it is a product sold in the country where that annex applies. Lists shown to the user
 * therefore group by country ("Denmark"), never by annex ("Danish annex").
 *
 * Label keys are `country_<x>_.label`, present in every app's locale files.
 */
export const ANNEX_COUNTRY_LABEL: Readonly<Record<ng.eurocode.Annex, string>> = {
  [ng.eurocode.Annex.ANNEX_UNSPECIFIED]: "country_common_.label",
  [ng.eurocode.Annex.ANNEX_COMMON]: "country_common_.label",
  [ng.eurocode.Annex.ANNEX_HUNGARY]: "country_hungary_.label",
  [ng.eurocode.Annex.ANNEX_ROMANIA]: "country_romania_.label",
  [ng.eurocode.Annex.ANNEX_DENMARK]: "country_denmark_.label",
  [ng.eurocode.Annex.ANNEX_SWEDEN]: "country_sweden_.label",
  [ng.eurocode.Annex.ANNEX_NORWAY]: "country_norway_.label",
  [ng.eurocode.Annex.ANNEX_FINLAND]: "country_finland_.label",
  [ng.eurocode.Annex.ANNEX_GREAT_BRITAIN]: "country_great_britain_.label",
  [ng.eurocode.Annex.ANNEX_GERMANY]: "country_germany_.label",
  [ng.eurocode.Annex.ANNEX_POLAND]: "country_poland_.label",
  [ng.eurocode.Annex.ANNEX_TURKEY]: "country_turkey_.label",
  [ng.eurocode.Annex.ANNEX_ESTONIA]: "country_estonia_.label",
  [ng.eurocode.Annex.ANNEX_LATVIA]: "country_latvia_.label",
  [ng.eurocode.Annex.ANNEX_BELGIUM]: "country_belgium_.label",
  [ng.eurocode.Annex.ANNEX_NETHERLAND]: "country_netherland_.label",
  [ng.eurocode.Annex.ANNEX_SPAIN]: "country_spain_.label",
};

const USER_DEFINED_LABEL = "user_defined_.label";

/** The subset of a materials-list entry needed to place it under a heading. */
export interface CountryGroupableMaterial {
  guid: string;
  name: string;
  /** Absent when the source does not know it (an older asset-api, or a project's own `mtrlDb`). */
  annex?: ng.eurocode.Annex;
  ownertype?: ng.utils.Owner;
}

export interface MaterialCountryGroup<T extends CountryGroupableMaterial> {
  /** Untranslated label key for the heading. */
  label: string;
  items: T[];
}

const isUserOwned = (owner: ng.utils.Owner | undefined): boolean =>
  owner === ng.utils.Owner.OWNER_USER ||
  owner === ng.utils.Owner.OWNER_OFFICE ||
  owner === ng.utils.Owner.OWNER_COMPANY;

/**
 * Groups materials under country headings: "Standard" first, then the countries in alphabetical
 * order of their translated names, then the user's own materials. Names sort naturally within a
 * group, so B500A < B500B < B500C < Ks600 and "Y8" < "Y10".
 *
 * Returns `null` when no entry carries an annex, so the caller can fall back to a plain sorted list
 * rather than put everything under "Standard" for want of information.
 *
 * Materials the user created are stored with the common annex, so without the owner check they
 * would land under "Standard" as if StruSoft supplied them.
 */
export function groupMaterialsByCountry<T extends CountryGroupableMaterial>(
  materials: readonly T[],
  translate: (key: string) => string,
  locale?: string,
): MaterialCountryGroup<T>[] | null {
  if (!materials.some((m) => m.annex !== undefined && m.annex !== null)) {
    return null;
  }

  const groups = new Map<string, T[]>();
  for (const material of materials) {
    const label = isUserOwned(material.ownertype)
      ? USER_DEFINED_LABEL
      : (ANNEX_COUNTRY_LABEL[material.annex as ng.eurocode.Annex] ??
        ANNEX_COUNTRY_LABEL[ng.eurocode.Annex.ANNEX_COMMON]);
    const items = groups.get(label) ?? [];
    items.push(material);
    groups.set(label, items);
  }

  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: "base" });
  const rank = (label: string): number =>
    label === ANNEX_COUNTRY_LABEL[ng.eurocode.Annex.ANNEX_COMMON] ? 0 : label === USER_DEFINED_LABEL ? 2 : 1;

  return [...groups.entries()]
    .map(([label, items]) => ({ label, items: items.sort((a, b) => collator.compare(a.name, b.name)) }))
    .sort((a, b) => rank(a.label) - rank(b.label) || collator.compare(translate(a.label), translate(b.label)));
}

/** Natural-order sort for a list with no country information. */
export function sortMaterialsByName<T extends { name: string }>(materials: readonly T[], locale?: string): T[] {
  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: "base" });
  return [...materials].sort((a, b) => collator.compare(a.name, b.name));
}
